/**
 * Simulated RingCentral.
 *
 * Two things live here:
 *
 *  1. `simulatedSmsThread()` — GET /api/sms/thread, the customer text thread
 *     drawer. Shape is what src/lib/api/sms.ts reads.
 *  2. `simulatedCallTiming()` — GET /api/rc/speed-to-lead, the owner's
 *     speed-to-lead timing model over the real `call_log` rows.
 *
 * Speed-to-lead constants are the owner's, not invented here:
 *     RING_SECONDS_PER_RING   6 s      (rings = ring_length_sec ÷ 6)
 *     ANSWERED_FAST_SEC      18 s      ("answered in 3 rings")
 *     CALLBACK_GREEN_SEC    600 s      (green callback)
 *     CALLBACK_WINDOW_SEC     8 h      (how far out a callback still counts)
 *     ARRIVAL_GREEN_SEC    3600 s      (truck on site within an hour of callback)
 *
 * NOTE for whoever reads the network tab: the Speed to Lead SCREEN does not go
 * through this endpoint — it reads `call_log` from the snapshot directly, so
 * its ring times are real production data. This endpoint exists so the timing
 * model above has a home; the callback and arrival stamps it reports are
 * generated, because the snapshot's calls are all inbound/missed and carry no
 * `arrived_on_site_at`.
 */
import { loadTable } from '~/demo/data';
import { once, seededInt } from './prng';
import { num, round2, str } from './finance';

export const RING_SECONDS_PER_RING = 6;
export const ANSWERED_FAST_SEC = 18;
export const CALLBACK_GREEN_SEC = 600;
export const CALLBACK_WINDOW_SEC = 8 * 60 * 60;
export const ARRIVAL_GREEN_SEC = 3600;

const digits = (v: unknown): string => str(v).replace(/\D+/g, '');

export const simulatedCallTiming = once(async () => {
  const calls = await loadTable('call_log');

  const rows = calls
    .filter((c) => str(c.started_at))
    .sort((a, b) => str(b.started_at).localeCompare(str(a.started_at)))
    .slice(0, 400)
    .map((c) => {
      const id = str(c.id);
      const ringSec = c.ring_length_sec == null ? null : num(c.ring_length_sec);
      const rings = ringSec == null ? null : Math.max(1, Math.round(ringSec / RING_SECONDS_PER_RING));
      const answeredFast = ringSec != null && ringSec <= ANSWERED_FAST_SEC;
      // Callback: generated, inside the 8-hour detection window.
      const callbackSec = seededInt(`${id}|callback`, 45, CALLBACK_WINDOW_SEC / 8);
      const arrivalSec = callbackSec + seededInt(`${id}|arrive`, 900, 9000);
      const started = new Date(str(c.started_at)).getTime();
      return {
        id,
        started_at: str(c.started_at),
        direction: str(c.direction),
        from_name: str(c.from_name) || null,
        from_number: digits(c.from_number) || null,
        duration_sec: num(c.duration_sec),
        ring_length_sec: ringSec,
        rings,
        answered_in_3_rings: answeredFast,
        callback_sec: callbackSec,
        callback_green: callbackSec <= CALLBACK_GREEN_SEC,
        callback_at: Number.isFinite(started) ? new Date(started + callbackSec * 1000).toISOString() : null,
        arrive_on_site_sec: arrivalSec,
        arrival_green: arrivalSec - callbackSec <= ARRIVAL_GREEN_SEC,
        is_lead: c.is_lead === true,
      };
    });

  const withRing = rows.filter((r) => r.ring_length_sec != null);
  const median = (values: number[]): number | null => {
    if (!values.length) return null;
    const s = [...values].sort((a, b) => a - b);
    return round2(s[Math.floor(s.length / 2)]);
  };

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Ring times are REAL call_log values scored at 6 s per ring (answered fast ≤ 18 s); callback (green ≤ 600 s, '
      + 'detected within 8 h) and truck arrival (green within 3600 s of the callback) are generated, because the '
      + 'snapshot holds no outbound calls and no arrival stamps.',
    constants: {
      ring_seconds_per_ring: RING_SECONDS_PER_RING,
      answered_fast_sec: ANSWERED_FAST_SEC,
      callback_green_sec: CALLBACK_GREEN_SEC,
      callback_window_sec: CALLBACK_WINDOW_SEC,
      arrival_green_sec: ARRIVAL_GREEN_SEC,
    },
    totals: {
      calls: rows.length,
      leads: rows.filter((r) => r.is_lead).length,
      answered_in_3_rings: withRing.filter((r) => r.answered_in_3_rings).length,
      answered_in_3_rings_pct: withRing.length
        ? round2((withRing.filter((r) => r.answered_in_3_rings).length / withRing.length) * 100) : 0,
      median_ring_sec: median(withRing.map((r) => r.ring_length_sec ?? 0)),
      median_callback_sec: median(rows.map((r) => r.callback_sec)),
      callback_green_pct: rows.length
        ? round2((rows.filter((r) => r.callback_green).length / rows.length) * 100) : 0,
      arrival_green_pct: rows.length
        ? round2((rows.filter((r) => r.arrival_green).length / rows.length) * 100) : 0,
    },
    calls: rows.slice(0, 100),
  };
});

/** GET /api/sms/thread?customer_id=… */
export async function simulatedSmsThread(customerId: string) {
  const [customers, jobs, calls] = await Promise.all([
    loadTable('customers'), loadTable('v_jobs_full'), loadTable('call_log'),
  ]);
  const customer = customers.find((c) => str(c.id) === customerId);
  const phones = Array.isArray(customer?.phones) ? (customer?.phones as unknown[]) : [];
  const phone = phones.length ? str(phones[0]) : null;
  const name = str(customer?.name);
  const job = jobs.find((j) => str(j.customer_id) === customerId);
  const jobNumber = str(job?.job_number);

  // Anchor the thread on a real call to/from this customer when there is one.
  const call = calls.find((c) => str(c.matched_customer_id) === customerId);
  const anchor = new Date(str(call?.started_at) || str(job?.received_at) || new Date().toISOString());
  const at = (offsetMin: number) => new Date(anchor.getTime() + offsetMin * 60_000).toISOString();

  const first = (name.split(' ')[0] || 'there').trim();
  const script: { direction: 'outbound' | 'inbound'; body: string; min: number }[] = [
    { direction: 'outbound', min: 4, body: `Hi ${first}, this is PDQ Restoration confirming we're on the way${jobNumber ? ` for ${jobNumber}` : ''}. Text this number if anything changes.` },
    { direction: 'inbound', min: 9, body: 'Thank you — someone will be here to let the crew in.' },
    { direction: 'outbound', min: 62, body: 'Crew is on site and has started setting equipment. We\'ll send a daily update while the dryers run.' },
    { direction: 'inbound', min: 1_450, body: 'Sounds good. Is anyone coming today to check the readings?' },
    { direction: 'outbound', min: 1_495, body: 'Yes — a tech is scheduled this morning to take moisture readings and adjust the equipment.' },
  ];

  // PDQ's own number is whatever the real inbound calls were dialled to.
  const pdqNumber = digits(call?.to_number) || digits(calls.find((c) => digits(c.to_number))?.to_number) || '';
  const customerNumber = digits(phone) || digits(call?.from_number) || '';

  const messages = script.map((m, i) => {
    const outbound = m.direction === 'outbound';
    return {
      id: `demo-sms-${customerId}-${i}`,
      direction: m.direction,
      body: m.body,
      from_number: outbound ? pdqNumber : customerNumber,
      to_number: outbound ? customerNumber : pdqNumber,
      sent_by_name: outbound ? 'PDQ Restoration' : null,
      status: outbound ? ('delivered' as const) : ('received' as const),
      occurred_at: at(m.min + seededInt(`${customerId}|${i}|drift`, 0, 6)),
      sent_by: null,
      job_id: str(job?.id) || null,
    };
  });

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Customer, phone and job number are real snapshot values and the thread is anchored to a real call_log '
      + 'timestamp; the message text itself is generated for the demo.',
    customer: { id: customerId, name, phone, e164: phone ? `+1${digits(phone)}`.slice(0, 12) : null },
    messages,
    sync: { ok: true, inserted: 0, updated: 0, simulated: true },
  };
}
